import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  X,
  Send,
  Sparkles,
  Minimize2,
  Maximize2,
  RotateCcw,
  AlertCircle,
} from 'lucide-react';
import { personalAiFetch, providerHasApiKey } from '../lib/aiProviderKeys';
import { callFeatureLlm } from '../lib/llmClient';
import { useHarryStore } from '../stores/prioritization/harryStore';
import { useUserStore } from '../stores/prioritization/userStore';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const STORAGE_KEY = 'peakPriorities_aiChatHistory_v2';

const SYSTEM_PROMPT = `You are the Priorities.ai Co-Facilitator — a sharp, practical expert in organizational prioritization methodology. You coach how to think about tradeoffs, methods, and decisions. You do NOT navigate the product UI or route users to screens (that is Guides / Navigator).

## Your role
You help teams and leaders:
- Clarify WHAT to prioritize and articulate WHY with precision
- Select the right prioritization method for their specific situation
- Interpret results of prioritization sessions into actionable insights
- Navigate misalignment between stated priorities and actual work
- Generate compelling narratives and justifications from priority decisions
- Think through the tradeoffs that hard prioritization decisions require

## The Peak Priorities Methodology

**Item Lifecycle**: Proto (raw idea captured quickly) → Item (shaped with attributes, ready to evaluate) → Priority (ranked in a Focus List)

**Item Pools**: Named, ordered lists with a cut line separating active work (above the line) from deferred work (below the line). The cut line is set based on real capacity.

**Prioritization Sessions**: Structured decision events using 20+ methods:
- Pairwise Ranking: Compare items two at a time — best for 5-20 items when you need strict ordering
- Dot Voting: Allocate votes across options — best for early narrowing and consensus signals
- Stack Ranking: Force-order all items — best for small lists with clear decision authority
- RICE Scoring: Reach × Impact × Confidence ÷ Effort — best for data-driven product teams
- Weighted Scoring: Multi-criteria with weights — best for complex strategic tradeoffs
- Buy-a-Feature: Allocate a budget — best for capturing stakeholder value signals
- MoSCoW: Must/Should/Could/Won't — best for scope-setting and release planning
- Impact-Effort Matrix: 2×2 quadrant — best for quick portfolio triage
- Cost of Delay: Quantify the cost of waiting — best for business case prioritization
- Eisenhower Matrix: Urgent vs Important — best for individual workload management

**Roles**:
- List Owner: Final decision authority for a Focus List
- Item Manager: Shapes raw Protos into evaluatable Items
- Criteria Authority: Defines what counts as better within a Track
- Facilitator: Guides the session process and pacing
- Cross-Map Lead: Coordinates alignment across multiple lists

**Catchball Loop** (advanced governance): Multi-level alignment where priorities travel up through org levels for review, return with revision guidance, and eventually receive Accept/Reject. Produces an immutable Plan of Record.

## What AI makes possible in prioritization
You can help users produce things they couldn't without you:
1. **Strategic narrative**: Turn a ranked list into a coherent, defensible rationale document
2. **Hidden criteria detection**: Identify the implicit values in how a group ranked things
3. **Socratic challenge**: Ask the hard questions that reveal priorities weren't as clear as assumed
4. **Async synthesis**: Synthesize inputs from stakeholders who couldn't participate live
5. **Stakeholder translation**: Reframe the same priority list for different audiences (exec, team, customer)
6. **Drift explanation**: Help articulate why work diverged from stated priorities

## Communication style
- Be direct and practical — no hedging, no padding
- When someone describes a prioritization challenge, ask one clarifying question before recommending
- Offer specific recommendations, not just frameworks
- When explaining priority tradeoffs, name the actual tension rather than abstracting it away
- Keep responses tight unless the user asks for depth`;

const QUICK_QUESTIONS = [
  "How do I choose the right prioritization method for my team?",
  "Help me write a rationale for our top 3 priorities",
  "We disagree on priorities — how do we resolve it?",
  "What makes a good cut line decision?",
  "How do I explain our priorities to executives?",
  "What should be above the line vs below it?",
];

function getPageContext(pathname: string): string {
  if (pathname.includes('/sessions')) return 'Prioritization Sessions page';
  if (pathname.includes('/items')) return 'Items page';
  if (pathname.includes('/focus-list')) return 'Focus Lists page';
  if (pathname.includes('/cycle')) return 'Prioritization Cycle configuration';
  if (pathname.includes('/catchball')) return 'Catchball Governance page';
  if (pathname.includes('/analytics') || pathname.includes('/insights')) return 'Analytics & Insights';
  if (pathname.includes('/intake')) return 'Item Intake';
  if (pathname.includes('/settings')) return 'Settings page';
  if (pathname.includes('/prioritization')) return 'Prioritization Dashboard';
  return 'Priorities.ai';
}

async function sendToAI(
  history: Message[],
  userMessage: string,
  pageContext: string,
): Promise<string> {
  // Our own Claude key serves whiteboard photo reads only (api/_lib/managedWhiteboard.js),
  // so the navigator chat runs on a person's own key.

  const contextualUserMessage = pageContext
    ? `[User is currently on: ${pageContext}]\n\n${userMessage}`
    : userMessage;

  if (providerHasApiKey('anthropic')) {
    // The person's own key, attached on the server (ai-proxy, migration 982).
    const response = await personalAiFetch('anthropic', {
      model: DEFAULT_MODEL,
      max_tokens: 1024,
      system: SYSTEM_PROMPT,
      messages: [
        ...history.map((m) => ({ role: m.role, content: m.content })),
        { role: 'user', content: contextualUserMessage },
      ],
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.error?.message || `AI error: ${response.status}`);
    }
    return data.content?.[0]?.text ?? '';
  }

  // Fall back to the shared LLM client (OpenAI, Peak LLM, etc.)
  const historyText =
    history.length > 0
      ? '\n\n---\nPrior conversation:\n' +
        history
          .map((m) => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`)
          .join('\n\n')
      : '';

  const result = await callFeatureLlm({
    userPrompt: contextualUserMessage + historyText,
    systemPrompt: SYSTEM_PROMPT,
    model: DEFAULT_MODEL,
    conversationId: 'ai-navigator-' + Date.now(),
    maxTokens: 1024,
  });

  if (!result) {
    throw new Error(
      'No AI provider configured. Add an Anthropic or OpenAI key in Settings → Connectors.',
    );
  }
  return result;
}

export const AINavigator: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [noProvider, setNoProvider] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const userId = useUserStore((s) => s.userId);
  const setUiPrefs = useHarryStore((s) => s.setUiPrefs);

  const switchToGuides = () => {
    setUiPrefs(userId, { cornerAssistant: 'guides' });
    setIsOpen(false);
  };

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setMessages(
          parsed.map((m: Message) => ({ ...m, timestamp: new Date(m.timestamp) })),
        );
      }
    } catch {
      // ignore parse errors
    }
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
      } catch {
        // ignore storage errors
      }
    }
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          role: 'assistant',
          content: `**Priorities.ai Co-Facilitator**

I'm your prioritization methodology coach — how to think about tradeoffs, methods, and decisions. I don't navigate the product UI (use Guides for that).

**Try asking:**
- "Help me pick a prioritization method for my team of 8"
- "Write a rationale for our top priorities"
- "We have 20 items — how do we narrow to 5?"
- "How do I handle disagreement about what matters most?"

What are you working through?`,
          timestamp: new Date(),
        },
      ]);
    }
  }, [isOpen, messages.length]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMsg: Message = {
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);
    setNoProvider(false);

    try {
      const pageContext = getPageContext(location.pathname);
      const reply = await sendToAI(messages, input, pageContext);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: reply, timestamp: new Date() },
      ]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      const isProviderError = msg.includes('No AI provider') || msg.includes('API key');
      if (isProviderError) setNoProvider(true);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: isProviderError
            ? `**No AI provider configured.**\n\nTo enable the co-facilitator:\n1. Go to **Settings → Connectors**\n2. Add your Anthropic API key (Claude) or OpenAI key\n3. Come back and ask me anything`
            : `Sorry, I hit an error: ${msg}`,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setNoProvider(false);
    localStorage.removeItem(STORAGE_KEY);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-gradient-to-r from-[var(--slate-blue,#4A5568)] to-[var(--alpine-green,#2F855A)] text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all z-50 group"
        title="Open Co-Facilitator — prioritization methodology coach"
        aria-label="Open Co-Facilitator — prioritization methodology coach"
      >
        <Sparkles className="h-6 w-6 group-hover:rotate-12 transition-transform" />
        <span className="absolute -top-1 -right-1 bg-[var(--luminous-coral,#FC8181)] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
          AI
        </span>
      </button>
    );
  }

  return (
    <div
      className={`fixed bottom-6 right-6 bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]  shadow-2xl border border-[var(--rule)] dark:border-[var(--rule)] z-50 flex flex-col transition-all ${
        isMinimized ? 'h-14' : 'h-[580px]'
      } w-[380px]`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-gradient-to-r from-slate-50 to-emerald-50 dark:from-[var(--bg-raised)] dark:to-[var(--bg-raised)] flex-shrink-0">
        <div className="flex items-center gap-2.5 min-w-0">
          <Sparkles className="h-5 w-5 text-[var(--green)] dark:text-[var(--text-ter)] shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] leading-none">
              Co-Facilitator
            </p>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
              Methodology coach — how to prioritize, not product navigation
            </p>
            <button
              type="button"
              onClick={switchToGuides}
              className="mt-1 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] underline-offset-2 hover:underline hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]"
            >
              Switch corner to Guides
            </button>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {messages.length > 1 && (
            <button
              onClick={clearChat}
              className="p-1.5 hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] rounded transition-colors"
              title="Clear conversation"
            >
              <RotateCcw className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
            </button>
          )}
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1.5 hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] rounded transition-colors"
          >
            {isMinimized ? (
              <Maximize2 className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
            ) : (
              <Minimize2 className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
            )}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 hover:bg-[var(--coral-dim)] dark:hover:bg-[var(--coral)]/20 rounded transition-colors"
          >
            <X className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* No provider warning */}
          {noProvider && (
            <div className="mx-3 mt-2 flex items-start gap-2 p-2.5 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border border-[var(--mustard)] dark:border-[var(--mustard)]/50 flex-shrink-0">
              <AlertCircle className="h-4 w-4 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
              <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
                Add an API key in{' '}
                <a href="/prioritization/settings/connectors" className="underline font-medium">
                  Settings → Connectors
                </a>{' '}
                to enable AI.
              </p>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0">
            {messages.map((message, idx) => (
              <div
                key={idx}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[88%]  px-3 py-2 text-sm ${
                    message.role === 'user'
                      ? 'bg-[var(--green)] text-white'
                      : 'bg-[var(--hover-tint)] dark:bg-[var(--bg-raised)] text-[var(--text-pri)] dark:text-[var(--text-pri)] border border-[var(--rule)] dark:border-[var(--rule)]'
                  }`}
                >
                  {message.role === 'assistant' ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0.5 prose-headings:mt-2 prose-headings:mb-1">
                      <ReactMarkdown
                        components={{
                          a: ({ ...props }) => (
                            <a
                              {...props}
                              className="text-[var(--green)] dark:text-[var(--text-ter)] underline"
                              target="_blank"
                              rel="noopener noreferrer"
                            />
                          ),
                          code: ({ ...props }) => (
                            <code
                              {...props}
                              className="bg-[var(--hover-tint)] px-1 py-0.5 rounded text-xs"
                            />
                          ),
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap">{message.content}</div>
                  )}
                  <div
                    className={`text-xs mt-1 ${
                      message.role === 'user'
                        ? 'text-[var(--green)] dark:text-[var(--text-ter)]'
                        : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'
                    }`}
                  >
                    {message.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              </div>
            ))}

            {/* Quick questions — shown after welcome, before user sends anything */}
            {messages.length === 1 && (
              <div className="grid grid-cols-1 gap-1.5">
                {QUICK_QUESTIONS.slice(0, 4).map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => setInput(q)}
                    className="text-left px-3 py-2 bg-[var(--surface)] dark:bg-[var(--bg)] hover:bg-[var(--green-dim)] dark:hover:bg-[var(--green)]/20 border border-[var(--rule)] dark:border-[var(--rule)] text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] hover:border-[var(--green)] transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-[var(--hover-tint)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <div className="flex space-x-1">
                      {[0, 150, 300].map((delay) => (
                        <div
                          key={delay}
                          className="w-1.5 h-1.5 bg-[var(--green)] rounded-full animate-bounce"
                          style={{ animationDelay: `${delay}ms` }}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                      Thinking…
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-[var(--rule)] dark:border-[var(--rule)] flex-shrink-0">
            <div className="flex gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask how to think about prioritization…"
                className="flex-1 px-3 py-2 border border-[var(--rule)] dark:border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-[var(--green)] resize-none text-sm bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 dark:placeholder-[var(--text-ter)]"
                rows={2}
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
                className={`p-2.5  transition-all self-end ${
                  input.trim() && !isLoading
                    ? 'bg-[var(--green)] hover:bg-[var(--green)] text-white shadow-sm'
                    : 'bg-[var(--hover-tint)] dark:bg-[var(--hover-tint)] text-[var(--text-ter)] cursor-not-allowed'
                }`}
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1.5 text-center">
              Enter to send · Shift+Enter for new line
            </p>
          </div>
        </>
      )}
    </div>
  );
};
